import { renderMetaTags, StructuredTextGraphQlResponse, TitleMetaLinkTag, useQuerySubscription } from "react-datocms";
import { GetStaticProps } from "next";
import { Api, IPlanObject } from "@/api";
import { Layout, SEO, SignUpForm, StartFreeButtons } from "@/components";
import { DATOCMS_API_TOKEN, IS_GOOGLE_AUTH_ENABLED } from "@/config";
import { getLiveSelectPlans } from "@/helpers";
import { metaTagsFragment, responsiveImageFragment, EditStylesHeroSection, EditStylesBodySection } from "@/modules";
import { SiteBanner } from "@/modules/site-banner";
import { ArtistStyle, Subscription } from "@/types";
import { PromoBanner } from "@narrative-software/narrative-web-ui";

interface IEditStylesPage {
  seo: TitleMetaLinkTag[];
  title: string;
  description: string;
  artistStyles: ArtistStyle[];
}

interface IEditStyles {
  page: IEditStylesPage;
  banner: {
    enabled: boolean;
    content: StructuredTextGraphQlResponse;
  };
}

type EditStylesPageProps = {
  preview: boolean;
  subscription: Subscription<IEditStyles>;
  plans: IPlanObject[];
};

const EditStylesPage = ({ preview, subscription }: EditStylesPageProps) => {
  const { data: { page: data, banner } = {} } = useQuerySubscription(subscription);

  if (!data) return null;

  const { seo, title, description } = data;

  return (
    <Layout product="edit" preview={preview} siteBanner={banner ? <SiteBanner {...banner} /> : null}>
      <SEO title="Narrative | Fast image editing | Photographers">{renderMetaTags(seo)}</SEO>
      <EditStylesHeroSection title={title} description={description} />
      <EditStylesBodySection artistStyles={data.artistStyles} />
      <PromoBanner
        title="Try our game changing software today"
        description="Get started with Narrative for free on macOS. No credit card required."
        form={
          IS_GOOGLE_AUTH_ENABLED ? (
            <StartFreeButtons product="edit" className="max-w-130" />
          ) : (
            <SignUpForm product="edit" className="max-w-160" textClassName="text-white" />
          )
        }
        backgroundColour="ekans"
        backgroundImagePath={"/images/textures/ekans-bg@2x.png"}
        large
      />
    </Layout>
  );
};

/**
 * Static props
 */
export const getStaticProps: GetStaticProps<EditStylesPageProps> = async ({ preview = false }) => {
  // Get plans
  const response = await Api.getPlans({ "filter[product-name]": "Select" });
  const plans = getLiveSelectPlans(response.data.data);

  const graphqlRequest = {
    preview,
    query: `
      {
        page: pageEditStyle {
          title
          description
          artistStyles {
            id
            name
            slug
            summary
            description
            image1After { 
              responsiveImage(imgixParams: { auto: format, w: 800, h: 600 }) {
                ...responsiveImageFragment
              }
            }
            artist {
              name
              profilePicture { 
                responsiveImage(imgixParams: { auto: format, w: 100, h: 100, fit: crop }) {
                  ...responsiveImageFragment
                }
              }
            }
          }
          seo: _seoMetaTags {
            ...metaTagsFragment
          }
        }
        banner: moduleSiteBanner {
          enabled
          content {
            value
          }
        }
      }
      ${metaTagsFragment}
      ${responsiveImageFragment}
    `,
  };

  const {
    data: { data: initialData },
  } = await Api.datocmsGraphqlRequest<IEditStyles>(graphqlRequest);

  return {
    props: {
      preview,
      plans,
      subscription: preview
        ? {
            ...graphqlRequest,
            initialData,
            token: DATOCMS_API_TOKEN,
          }
        : {
            initialData,
            enabled: false,
          },
    },
  };
};

export default EditStylesPage;
